/**
 * createEngine(): the ViewerEngine implementation. Owns the canvas, the Three.js context, the scene root (which the
 * trackball moves), the MSAA target and the frame loop (redraws only when dirty).
 *
 * Frame order: shadow depth pass (light space, 2048, quality high/medium) -> skybox -> opaque ribbons/surfaces ->
 * impostors -> transparent surfaces (back, front) -> highlight mask / band / shell -> ribbon inverse hull -> labels ->
 * entry hover box -> MSAA resolve -> sRGB encode (PLAN.md "Renderer frame order").
 */
import {
  Camera, DepthTexture, Group, LessEqualCompare, LinearFilter, Matrix4, PerspectiveCamera, RedFormat, RGBAFormat, Scene, SRGBColorSpace,
  UnsignedByteType, UnsignedIntType, Vector3, WebGLRenderTarget, WebGLRenderer,
} from 'three';
import type { Component, Entry, SelectionMode, Tool } from '@nanome-lite/state';
import type { HoverInfo, PickResult, SnapshotOptions, ViewerEngine, ViewerEngineEvents } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { CAMERA, INTERACTION, LIGHTING, SKYBOX_DEFAULT_KEY, SKYBOX_NIGHT_MODE_DEFAULT } from '@nanome-lite/look';
import { autoZoomTransform, easeInOut, focusTransform, lerpSceneTransform, mergeBounds, perspective, projectToScreen, type Bounds } from './camera';
import { ComponentManager, type AsyncRibbonBuilder, type FilterEvaluator, type PassSet, type QualityTier, type SurfaceProvider } from './components';
import { EntryHoverBox, rayAabb, screenCoverage } from './entry-box';
import { SHADOW_DISTANCE_METRES, SHADOW_MAP_SIZE, SHADOW_STRENGTH, boxCornersToView, cameraSliceCorners, fitShadowFrustum, shadowCasterBias, shadowFadeParams } from './shadow';
import { cloneSceneTransform, copySceneTransform, createSceneTransform, domainToWorld, sceneRootMatrix, type SceneTransform } from './frame';
import { DEPTH_BAND_MODEL_UNITS, createSharedUniforms } from './gl';
import { HighlightLayer, buildShellInstances, type ShellInstances } from './highlight';
import { HOVER_COLOR, ImpostorMaterials, SELECTION_COLOR } from './impostors';
import { HoverLabelOverlay, RESIDUE_LABEL_LINE_HEIGHT_ANGSTROM, formatHoverLabel } from './labels';
import { HOVER_INTERVAL_MS, PickPass, PickSlots, type PickHit } from './picking';
import { fallbackRibbonBuilder } from './ribbon-fallback';
import { MeshMaterials } from './ribbon-pass';
import { Skybox, createComposite } from './skybox';
import { renderSnapshot } from './snapshot';
import { TrackballController } from './trackball';

export interface EngineOptions {
  /** MSAA sample count for the scene target; 0 disables. Default LIGHTING.msaa (4). */
  msaaSamples?: number;
  /** Residue label line height in Å. Default: TextMeshPro fontSize 12 x 0.1 x scale 0.6 = 0.72 Å. */
  residueLabelHeightAngstrom?: number;
  /** Override window.devicePixelRatio. */
  devicePixelRatio?: number;
  skyboxTheme?: string;
  nightMode?: boolean;
  /** Focus animation length (ZoomOnEntry). */
  focusAnimationMs?: number;
  /** DOM wheel px -> Unity scroll units (default WHEEL_DELTA_TO_SCROLL = 0.01: a 100 px notch = 1.01^3, see trackball.ts). */
  wheelDeltaToScroll?: number;
  /** Main-light shadows on the high/medium tiers (default LIGHTING.shadows.enabled). */
  shadows?: boolean;
}

/** Shadow casters pad: atom centres -> vdW spheres and surfaces (probe 1.4 + radius) stay inside the light frustum. */
const SHADOW_BOUNDS_PAD_ANGSTROM = 4;

/** ViewerEngine plus the host callbacks Lite needs (data runs in a worker; surfaces come from packages/surface). */
export interface LiteEngine extends ViewerEngine {
  setFilterEvaluator(fn: FilterEvaluator | null): void;
  setSurfaceProvider(fn: SurfaceProvider | null): void;
  /** Sync builders (the packages/state contract) or async ones (a worker client); null restores the fallback. */
  setRibbonBuilder(fn: AsyncRibbonBuilder | null): void;
  requestRender(): void;
  readonly transform: SceneTransform;
  readonly canvas: HTMLCanvasElement | null;
}

type Handler<K extends keyof ViewerEngineEvents> = ViewerEngineEvents[K];

export function createEngine(options: EngineOptions = {}): LiteEngine {
  return new Engine(options);
}

class Engine implements LiteEngine {
  readonly transform = createSceneTransform();
  canvas: HTMLCanvasElement | null = null;
  private container: HTMLElement | null = null;
  private renderer: WebGLRenderer | null = null;
  private readonly scene = new Scene();
  private readonly root = new Group();
  private readonly pickScene = new Scene();
  private readonly pickRoot = new Group();
  private readonly shadowScene = new Scene();
  private readonly shadowRoot = new Group();
  private readonly shadowCamera = new Camera();
  private shadowTarget: WebGLRenderTarget | null = null;
  /** root matrix + aspect + scene version of the last shadow map; the map is only redrawn when one changes */
  private readonly shadowKey: number[] = [];
  private sceneVersion = 0;
  private readonly entryBox = new EntryHoverBox();
  private readonly camera = new PerspectiveCamera();
  private readonly shared = createSharedUniforms();
  private readonly impostors = new ImpostorMaterials(this.shared);
  private readonly meshes = new MeshMaterials(this.shared);
  private readonly skybox = new Skybox();
  private readonly composite = createComposite();
  private readonly compositeScene = new Scene();
  private readonly slots = new PickSlots();
  private readonly pickPass = new PickPass(this.slots);
  private readonly manager: ComponentManager;
  private readonly hoverLayer = new HighlightLayer(this.impostors, HOVER_COLOR);
  private readonly selectionLayer = new HighlightLayer(this.impostors, SELECTION_COLOR);
  private readonly handlers: { [K in keyof ViewerEngineEvents]: Set<Handler<K>> } = { hover: new Set(), pick: new Set(), ready: new Set(), error: new Set() };
  private target: WebGLRenderTarget | null = null;
  private msaa: number;
  private quality: QualityTier = 'high';
  private dirty = true;
  private raf = 0;
  private contextLost = false;
  private width = 1;
  private height = 1;
  private dpr: number;
  private resizeObserver: ResizeObserver | null = null;
  private trackball: TrackballController | null = null;
  private overlay: HoverLabelOverlay | null = null;
  private dragging = false;
  private tool: Tool = 'default';
  private mode: SelectionMode = 'residue';
  private selection: Record<number, number[]> = {};
  private hover: { info: HoverInfo; world: Vector3 } | null = null;
  /** residues highlighted by highlightResidues (the sequence view) */
  private externalHover: { entrySerial: number; residues: Set<number> } | null = null;
  private hoverPending: { x: number; y: number } | null = null;
  private hoverTimer = 0;
  private lastHoverAt = -Infinity;
  private focusAnim: { from: SceneTransform; to: SceneTransform; start: number; ms: number } | null = null;
  private readonly projection = new Matrix4();
  private readonly options: EngineOptions;
  private disposed = false;

  constructor(options: EngineOptions) {
    this.options = options;
    this.msaa = options.msaaSamples ?? LIGHTING.msaa;
    this.dpr = options.devicePixelRatio ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1);
    this.skybox.theme = options.skyboxTheme ?? SKYBOX_DEFAULT_KEY;
    this.skybox.nightMode = options.nightMode ?? SKYBOX_NIGHT_MODE_DEFAULT;
    this.root.matrixAutoUpdate = false;
    this.pickRoot.matrixAutoUpdate = false;
    this.shadowRoot.matrixAutoUpdate = false;
    this.shadowCamera.matrixAutoUpdate = false;
    this.shadowCamera.matrixWorldAutoUpdate = false;
    this.scene.add(this.skybox.mesh, this.root);
    this.root.add(this.hoverLayer.group, this.selectionLayer.group, this.entryBox.lines);
    this.pickScene.add(this.pickRoot);
    this.shadowScene.add(this.shadowRoot);
    this.compositeScene.add(this.composite.mesh);
    this.manager = new ComponentManager({
      root: this.root, pickScene: this.pickRoot, shadowScene: this.shadowRoot, impostors: this.impostors, meshes: this.meshes, shared: this.shared, slots: this.slots,
      onChange: () => { this.sceneVersion++; this.refreshHighlights(); this.requestRender(); },
      onError: (m) => this.emit('error', m),
      residueLabelHeightAngstrom: options.residueLabelHeightAngstrom ?? RESIDUE_LABEL_LINE_HEIGHT_ANGSTROM,
    });
  }

  // ---- lifecycle -------------------------------------------------------------------------------------------------

  mount(container: HTMLElement): void {
    if (this.canvas) throw new Error('engine already mounted');
    this.container = container;
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    const canvas = document.createElement('canvas');
    Object.assign(canvas.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block', outline: 'none' });
    container.appendChild(canvas);
    this.canvas = canvas;
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.contextLost = true; }, false);
    canvas.addEventListener('webglcontextrestored', () => { this.onContextRestored(); }, false);
    let renderer: WebGLRenderer;
    try {
      renderer = new WebGLRenderer({ canvas, antialias: false, alpha: false, premultipliedAlpha: false, stencil: true, depth: true, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    } catch (e) {
      this.emit('error', `WebGL2 unavailable: ${String(e)}`);
      return;
    }
    if (!renderer.capabilities.isWebGL2) { this.emit('error', 'WebGL2 is required'); }
    renderer.autoClear = false;
    renderer.setPixelRatio(1); // we manage DPR ourselves
    this.renderer = renderer;
    this.overlay = new HoverLabelOverlay(container);
    this.trackball = new TrackballController(canvas, {
      transform: this.transform,
      pivotAt: (x, y) => this.pivotAt(x, y),
      changed: () => { this.focusAnim = null; this.requestRender(); },
      setDragging: (d) => { this.dragging = d; if (d) this.setHover(null); },
      click: (x, y, ev) => this.onClick(x, y, ev),
      hover: (x, y) => this.queueHover(x, y),
      hoverEnd: () => { this.setHover(null); if (this.entryBox.set(null)) this.requestRender(); },
      ...(this.options.wheelDeltaToScroll !== undefined ? { wheelDeltaToScroll: this.options.wheelDeltaToScroll } : {}),
    });
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.loop();
    this.emit('ready');
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    clearTimeout(this.hoverTimer);
    this.resizeObserver?.disconnect();
    this.trackball?.dispose();
    this.overlay?.dispose();
    this.manager.dispose();
    this.hoverLayer.dispose();
    this.selectionLayer.dispose();
    this.impostors.dispose();
    this.meshes.dispose();
    this.skybox.dispose();
    this.composite.dispose();
    this.pickPass.dispose();
    this.entryBox.dispose();
    this.shadowTarget?.depthTexture?.dispose();
    this.shadowTarget?.dispose();
    this.target?.dispose();
    this.renderer?.dispose();
    this.canvas?.remove();
    this.canvas = null;
    this.renderer = null;
  }

  on<K extends keyof ViewerEngineEvents>(event: K, handler: ViewerEngineEvents[K]): () => void {
    (this.handlers[event] as Set<Handler<K>>).add(handler);
    return () => { (this.handlers[event] as Set<Handler<K>>).delete(handler); };
  }

  private emit<K extends keyof ViewerEngineEvents>(event: K, ...args: Parameters<ViewerEngineEvents[K]>): void {
    for (const h of this.handlers[event] as Set<Handler<K>>) (h as (...a: Parameters<ViewerEngineEvents[K]>) => void)(...args);
  }

  private onContextRestored(): void {
    this.contextLost = false;
    this.target?.dispose();
    this.target = null;
    this.disposeShadowTarget();
    this.skybox.invalidate();
    this.requestRender();
  }

  private resize(): void {
    if (!this.container || !this.canvas || !this.renderer) return;
    const rect = this.container.getBoundingClientRect();
    const dpr = this.options.devicePixelRatio ?? window.devicePixelRatio ?? 1;
    const w = Math.max(1, Math.round(rect.width * dpr)), h = Math.max(1, Math.round(rect.height * dpr));
    if (w === this.width && h === this.height && dpr === this.dpr) return;
    this.width = w; this.height = h; this.dpr = dpr;
    this.renderer.setSize(w, h, false);
    this.canvas.style.width = `${rect.width}px`; this.canvas.style.height = `${rect.height}px`;
    this.target?.dispose();
    this.target = null;
    this.requestRender();
  }

  requestRender(): void { this.dirty = true; }

  private loop = (): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    if (this.focusAnim) this.dirty = true;
    if (!this.dirty || this.contextLost || !this.renderer) return;
    this.dirty = false;
    try { this.renderFrame(); } catch (e) { this.emit('error', `render failed: ${String(e)}`); }
  };

  private ensureTarget(): WebGLRenderTarget | null {
    if (this.msaa <= 0) return null;
    if (!this.target) {
      this.target = new WebGLRenderTarget(this.width, this.height, { format: RGBAFormat, type: UnsignedByteType, depthBuffer: true, stencilBuffer: true, samples: this.msaa, colorSpace: SRGBColorSpace });
    }
    return this.target;
  }

  private updateFocusAnimation(): void {
    const a = this.focusAnim;
    if (!a) return;
    const t = (performance.now() - a.start) / a.ms;
    if (t >= 1) { copySceneTransform(this.transform, a.to); this.focusAnim = null; return; }
    lerpSceneTransform(a.from, a.to, easeInOut(t), this.transform);
  }

  private prepareUniforms(w: number, h: number): void {
    this.shared.uResolution.value.set(w, h);
    this.shared.uScale.value = this.transform.scale;
    this.shared.uDepthBand.value = DEPTH_BAND_MODEL_UNITS * this.transform.scale;
  }

  private updateRoot(): void {
    sceneRootMatrix(this.transform, this.root.matrix);
    this.root.matrixWorldNeedsUpdate = true;
    this.pickRoot.matrix.copy(this.root.matrix);
    this.pickRoot.matrixWorldNeedsUpdate = true;
    this.shadowRoot.matrix.copy(this.root.matrix);
    this.shadowRoot.matrixWorldNeedsUpdate = true;
  }

  // ---- shadows ---------------------------------------------------------------------------------------------------

  private shadowsActive(): boolean {
    return (this.options.shadows ?? LIGHTING.shadows.enabled) && (this.quality === 'high' || this.quality === 'medium');
  }

  private disposeShadowTarget(): void {
    this.shadowTarget?.depthTexture?.dispose();
    this.shadowTarget?.dispose();
    this.shadowTarget = null;
    this.shadowKey.length = 0;
    this.shared.uShadowMap.value = null;
  }

  private ensureShadowTarget(): WebGLRenderTarget {
    if (!this.shadowTarget) {
      const depth = new DepthTexture(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE, UnsignedIntType);
      // Hardware PCF: compare mode + bilinear filtering on a sampler2DShadow.
      depth.compareFunction = LessEqualCompare;
      depth.minFilter = LinearFilter;
      depth.magFilter = LinearFilter;
      this.shadowTarget = new WebGLRenderTarget(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE, { format: RedFormat, type: UnsignedByteType, depthBuffer: true, stencilBuffer: false, depthTexture: depth });
    }
    return this.shadowTarget;
  }

  /** Light-frustum fit + depth pass. Skipped when neither the scene transform, the aspect nor the scene changed. */
  private updateShadows(aspect: number, force = false): void {
    const u = this.shared;
    if (!this.shadowsActive() || !this.renderer) { u.uShadowParams.value.x = 0; return; }
    const e = this.root.matrix.elements;
    const key = [...e, aspect, this.sceneVersion];
    if (!force && this.shadowKey.length === key.length && key.every((v, i) => v === this.shadowKey[i])) return;
    this.shadowKey.length = 0; this.shadowKey.push(...key);
    const corners: Vector3[] = [];
    const visible = new Set<number>();
    for (const p of this.manager.passes()) if (!this.manager.isHidden(p.componentSerial)) visible.add(p.entrySerial);
    for (const es of visible) {
      const b = this.manager.entries.get(es)?.bounds;
      if (b && b.count > 0) corners.push(...boxCornersToView(b.min, b.max, SHADOW_BOUNDS_PAD_ANGSTROM, this.root.matrix));
    }
    const fit = fitShadowFrustum(corners, cameraSliceCorners(aspect, CAMERA.near, Math.min(CAMERA.far, SHADOW_DISTANCE_METRES)), u.uLightDir.value, SHADOW_MAP_SIZE);
    if (!fit) { u.uShadowParams.value.x = 0; return; }
    const target = this.ensureShadowTarget();
    const cam = this.shadowCamera;
    cam.matrixWorld.copy(fit.viewInverse);
    cam.matrixWorldInverse.copy(fit.view);
    cam.projectionMatrix.copy(fit.projection);
    cam.projectionMatrixInverse.copy(fit.projection).invert();
    const bias = shadowCasterBias(fit.texelSize);
    u.uShadowBias.value.set(bias.depth, bias.normal);
    u.uShadowMapSize.value = SHADOW_MAP_SIZE;
    const renderer = this.renderer;
    const prev = renderer.getRenderTarget();
    // The depth texture must not be bound for sampling while it is the attachment being written.
    u.uShadowMap.value = null;
    u.uShadowParams.value.x = 0;
    renderer.setRenderTarget(target);
    renderer.setClearColor(0xffffff, 1);
    renderer.clear(true, true, false);
    renderer.render(this.shadowScene, cam);
    renderer.setRenderTarget(prev);
    u.uShadowMap.value = target.depthTexture;
    u.uShadowMatrix.value.copy(fit.matrix);
    u.uShadowParams.value.set(1, SHADOW_STRENGTH, 0.5 / SHADOW_MAP_SIZE, 0);
    const [fs, fo] = shadowFadeParams();
    u.uShadowFade.value.set(fs, fo);
  }

  private renderFrame(): void {
    const renderer = this.renderer!;
    this.updateFocusAnimation();
    perspective(this.width / this.height, this.projection);
    this.camera.projectionMatrix.copy(this.projection);
    this.camera.projectionMatrixInverse.copy(this.projection).invert();
    this.updateRoot();
    this.updateShadows(this.width / this.height);
    this.prepareUniforms(this.width, this.height);
    this.skybox.prepare(renderer, this.projection);
    const target = this.ensureTarget();
    renderer.setRenderTarget(target);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);
    renderer.render(this.scene, this.camera);
    if (target) {
      renderer.setRenderTarget(null);
      this.composite.setTexture(target.texture);
      renderer.render(this.compositeScene, this.camera);
    }
    this.updateHoverLabelPosition();
  }

  // ---- entries / components --------------------------------------------------------------------------------------

  setEntry(entry: Entry, model: NanomeModel): void {
    const first = this.manager.entries.size === 0;
    this.setHover(null);
    if (this.entryBox.entrySerial === entry.serial) this.entryBox.invalidate();
    this.manager.setEntry(entry, model);
    if (first) this.resetView();
  }

  removeEntry(entrySerial: number): void {
    this.manager.removeEntry(entrySerial);
    if (this.externalHover?.entrySerial === entrySerial) this.externalHover = null;
    this.setHover(null);
    if (this.entryBox.entrySerial === entrySerial) this.entryBox.set(null);
  }
  setCurrentModel(entrySerial: number, modelSerial: number): void {
    if (this.entryBox.entrySerial === entrySerial) this.entryBox.invalidate();
    this.manager.setCurrentModel(entrySerial, modelSerial);
  }
  setComponents(components: Component[]): void { this.manager.setComponents(components); }
  setFilterEvaluator(fn: FilterEvaluator | null): void { this.manager.filterEvaluator = fn; this.manager.rebuildEverything(); }
  setSurfaceProvider(fn: SurfaceProvider | null): void { this.manager.surfaceProvider = fn; this.manager.rebuildEverything(); }
  setRibbonBuilder(fn: AsyncRibbonBuilder | null): void { this.manager.ribbonBuilder = fn ?? fallbackRibbonBuilder; this.manager.rebuildEverything(); }

  setSelection(byEntry: Record<number, number[]>): void {
    this.selection = byEntry;
    this.refreshHighlights();
    this.requestRender();
  }

  setTool(tool: Tool, mode: SelectionMode): void { this.tool = tool; this.mode = mode; this.refreshHighlights(); this.requestRender(); }

  highlightResidues(entrySerial: number, residueIndices: number[] | null): void {
    const next = residueIndices?.length ? { entrySerial, residues: new Set(residueIndices) } : null;
    if (!next && !this.externalHover) return;
    this.externalHover = next;
    this.refreshHighlights();
    this.requestRender();
  }

  // ---- camera ----------------------------------------------------------------------------------------------------

  private allBounds(): Bounds { return mergeBounds([...this.manager.entries.values()].map((r) => r.bounds)); }

  private animateTo(to: SceneTransform): void {
    this.focusAnim = { from: cloneSceneTransform(this.transform), to, start: performance.now(), ms: this.options.focusAnimationMs ?? 150 };
    this.requestRender();
  }

  focusEntry(entrySerial: number): void {
    const rec = this.manager.entries.get(entrySerial);
    if (rec) this.animateTo(focusTransform(rec.bounds, this.transform, createSceneTransform()));
  }

  focusComponent(componentSerial: number): void {
    const b = this.manager.componentBounds(componentSerial);
    if (b && b.count > 0) this.animateTo(focusTransform(b, this.transform, createSceneTransform()));
  }

  focusAll(): void {
    const b = this.allBounds();
    if (b.count > 0) this.animateTo(focusTransform(b, this.transform, createSceneTransform()));
  }

  /** SceneAutoZoom: identity rotation, centroid 0.4 m in front of the camera, scale to fit. */
  resetView(): void {
    const b = this.allBounds();
    this.focusAnim = null;
    autoZoomTransform(b, this.transform);
    this.requestRender();
  }

  // ---- environment -----------------------------------------------------------------------------------------------

  setSkybox(theme: string, nightMode: boolean): void { this.skybox.theme = theme; this.skybox.nightMode = nightMode; this.requestRender(); }

  setQuality(tier: QualityTier): void {
    this.quality = tier;
    if (!this.shadowsActive()) this.disposeShadowTarget();
    this.shadowKey.length = 0;
    const msaa = tier === 'high' || tier === 'medium' ? (this.options.msaaSamples ?? LIGHTING.msaa) : 0;
    if (msaa !== this.msaa) { this.msaa = msaa; this.target?.dispose(); this.target = null; }
    this.manager.setQuality(tier);
    this.requestRender();
  }

  async snapshot(opts: SnapshotOptions): Promise<Blob> {
    if (!this.renderer) throw new Error('engine not mounted');
    this.updateRoot();
    // The shadow frustum depends on the aspect; refit for the snapshot and refit back on the next frame.
    this.updateShadows(opts.width / opts.height, true);
    this.shadowKey.length = 0;
    const hoverVisible = this.hoverLayer.group.visible;
    const boxVisible = this.entryBox.lines.visible;
    if (opts.includeAnnotations === false) { this.hoverLayer.group.visible = false; this.entryBox.lines.visible = false; }
    try {
      return await renderSnapshot({
        renderer: this.renderer, scene: this.scene, camera: this.camera, projection: perspective(opts.width / opts.height), samples: this.msaa,
        includeBackground: opts.includeBackground, prepare: (w, h) => this.prepareUniforms(w, h), setSkyboxVisible: (v) => { this.skybox.mesh.visible = v; },
      }, opts.width, opts.height);
    } finally {
      this.hoverLayer.group.visible = hoverVisible;
      this.entryBox.lines.visible = boxVisible;
      this.requestRender();
    }
  }

  // ---- picking, hover, highlight --------------------------------------------------------------------------------

  private clientToPixel(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.canvas) return null;
    const rect = this.canvas.getBoundingClientRect();
    return { x: (clientX - rect.left) * this.dpr, y: (clientY - rect.top) * this.dpr };
  }

  private pickAt(clientX: number, clientY: number): PickHit | null {
    if (!this.renderer || this.contextLost) return null;
    const p = this.clientToPixel(clientX, clientY);
    if (!p) return null;
    this.updateRoot();
    perspective(this.width / this.height, this.projection);
    const hit = this.pickPass.pick(this.renderer, this.pickScene, this.camera, this.projection, p.x, p.y, this.width, this.height, (w, h) => this.prepareUniforms(w, h));
    this.prepareUniforms(this.width, this.height);
    return hit;
  }

  private resolveHit(hit: PickHit): { info: HoverInfo; world: Vector3; model: NanomeModel } | null {
    const rec = this.manager.entries.get(hit.object.entrySerial);
    if (!rec) return null;
    const { model, entry } = rec;
    let atomIndex: number | null = null, residueIndex: number, bondIndex: number | undefined, labelAtom: number;
    if (hit.object.kind === 'ribbon') {
      residueIndex = hit.index;
      if (residueIndex >= model.residues.count) return null;
      labelAtom = this.representativeAtom(model, residueIndex);
    } else if (hit.object.kind === 'bond') {
      bondIndex = hit.index;
      if (bondIndex >= model.bonds.count) return null;
      atomIndex = model.bonds.a[bondIndex]!;
      residueIndex = model.residueIndex[atomIndex]!;
      labelAtom = atomIndex;
    } else {
      atomIndex = hit.index;
      if (atomIndex >= model.atomCount) return null;
      residueIndex = model.residueIndex[atomIndex]!;
      labelAtom = atomIndex;
    }
    const world = domainToWorld(this.transform, model.xyz[labelAtom * 3]!, model.xyz[labelAtom * 3 + 1]!, model.xyz[labelAtom * 3 + 2]!);
    const screen = projectToScreen(world, this.projection, this.width, this.height);
    const info: HoverInfo = {
      entrySerial: entry.serial, atomIndex, residueIndex, label: formatHoverLabel(model, labelAtom, entry.name, hit.object.kind === 'ribbon'),
      screenX: screen ? screen.x / this.dpr : 0, screenY: screen ? screen.y / this.dpr : 0,
    };
    if (bondIndex !== undefined) info.bondIndex = bondIndex;
    return { info, world, model };
  }

  private representativeAtom(model: NanomeModel, residueIndex: number): number {
    const start = model.residues.atomStart[residueIndex]!, count = model.residues.atomCount[residueIndex]!;
    for (let i = start; i < start + count; i++) if (model.atomName[i] === 'CA' || model.atomName[i] === "C5'") return i;
    return start;
  }

  private queueHover(clientX: number, clientY: number): void {
    if (this.dragging) return;
    this.hoverPending = { x: clientX, y: clientY };
    const wait = Math.max(0, HOVER_INTERVAL_MS - (performance.now() - this.lastHoverAt));
    if (this.hoverTimer) return;
    this.hoverTimer = window.setTimeout(() => {
      this.hoverTimer = 0;
      const p = this.hoverPending;
      this.hoverPending = null;
      if (!p || this.dragging) return;
      this.lastHoverAt = performance.now();
      if (this.updateHoveredEntry(p.x, p.y)) this.requestRender();
      const hit = this.pickAt(p.x, p.y);
      const r = hit ? this.resolveHit(hit) : null;
      this.setHover(r ? { info: r.info, world: r.world } : null);
    }, wait);
  }

  private setHover(h: { info: HoverInfo; world: Vector3 } | null): void {
    const prev = this.hover;
    const same = prev && h && prev.info.entrySerial === h.info.entrySerial && prev.info.atomIndex === h.info.atomIndex && prev.info.residueIndex === h.info.residueIndex && prev.info.bondIndex === h.info.bondIndex;
    this.hover = h;
    if (same) return;
    this.refreshHighlights();
    if (h) this.overlay?.show(h.info.label, h.info.screenX, h.info.screenY); else this.overlay?.hide();
    this.requestRender();
    this.emit('hover', h ? h.info : null);
  }

  private updateHoverLabelPosition(): void {
    const h = this.hover;
    if (!h || !this.overlay) return;
    const rec = this.manager.entries.get(h.info.entrySerial);
    if (!rec) return;
    const atom = h.info.atomIndex ?? this.representativeAtom(rec.model, h.info.residueIndex);
    domainToWorld(this.transform, rec.model.xyz[atom * 3]!, rec.model.xyz[atom * 3 + 1]!, rec.model.xyz[atom * 3 + 2]!, h.world);
    const s = projectToScreen(h.world, this.projection, this.width, this.height);
    if (s) { h.info.screenX = s.x / this.dpr; h.info.screenY = s.y / this.dpr; this.overlay.show(h.info.label, h.info.screenX, h.info.screenY); }
  }

  private onClick(clientX: number, clientY: number, ev: { shiftKey: boolean; ctrlKey: boolean }): void {
    const hit = this.pickAt(clientX, clientY);
    const r = hit ? this.resolveHit(hit) : null;
    const result: PickResult | null = r ? { entrySerial: r.info.entrySerial, atomIndex: r.info.atomIndex, residueIndex: r.info.residueIndex, ...(r.info.bondIndex !== undefined ? { bondIndex: r.info.bondIndex } : {}) } : null;
    this.emit('pick', result, ev);
  }

  /** Expand a hit to the atoms the current tool highlights (DefaultPointerController.ExpandByResidue; Selection modes). */
  private expandAtoms(model: NanomeModel, atomIndex: number | null, residueIndex: number): Set<number> {
    const mode: SelectionMode = this.tool === 'default' ? 'residue' : this.mode;
    const out = new Set<number>();
    if (mode === 'atom' && atomIndex !== null) { out.add(atomIndex); return out; }
    if (mode === 'chain') {
      const chain = model.residues.chainIndex[residueIndex]!;
      for (let i = 0; i < model.atomCount; i++) if (model.chainIndex[i] === chain) out.add(i);
      return out;
    }
    const start = model.residues.atomStart[residueIndex]!, count = model.residues.atomCount[residueIndex]!;
    for (let i = start; i < start + count; i++) out.add(i);
    return out;
  }

  private shellsFor(entrySerial: number, atoms: Set<number>, residues: Set<number>, hull: 'hover' | 'selection'): ShellInstances[] {
    const rec = this.manager.entries.get(entrySerial);
    const shells: ShellInstances[] = [];
    if (!rec) return shells;
    const model = rec.model;
    const bondAtoms = (bi: number): [number, number] => [model.bonds.a[bi]!, model.bonds.b[bi]!];
    const hasBond = (i: number) => model.bondCount[i]! > 0;
    for (const p of this.manager.passes()) {
      if (p.entrySerial !== entrySerial || this.manager.isHidden(p.componentSerial)) continue;
      if (p.atomistic) shells.push(buildShellInstances(p.atomistic.geometry, atoms, p.atomistic.bondScale, hasBond, bondAtoms));
      if (p.ribbons) for (const r of p.ribbons) r.setHighlight(hull, residues.size ? residues : null);
    }
    return shells;
  }

  /** Rebuild hover and selection layers from the current hover, selection and tool. */
  private refreshHighlights(): void {
    // selection: white shells for every selected atom per entry
    const selShells: ShellInstances[] = [];
    const selectedEntries = new Set<number>();
    for (const [es, atoms] of Object.entries(this.selection)) {
      const set = new Set(atoms);
      if (set.size === 0) continue;
      const rec = this.manager.entries.get(Number(es));
      if (!rec) continue;
      selectedEntries.add(Number(es));
      const residues = new Set<number>();
      for (const i of set) residues.add(rec.model.residueIndex[i]!);
      selShells.push(...this.shellsFor(Number(es), set, residues, 'selection'));
    }
    for (const p of this.manager.passes()) if (p.ribbons && !selectedEntries.has(p.entrySerial)) for (const r of p.ribbons) r.setHighlight('selection', null);
    this.selectionLayer.set(selShells);
    // hover: per tool expansion; atom hover outlines atoms, ribbon hover outlines the ribbon (EHighlightTarget)
    const h = this.hover;
    let hoverShells: ShellInstances[] = [];
    let hoveredRibbonsCleared = false;
    if (h) {
      const rec = this.manager.entries.get(h.info.entrySerial);
      if (rec) {
        const atoms = this.expandAtoms(rec.model, h.info.atomIndex, h.info.residueIndex);
        const residues = new Set<number>();
        if (h.info.atomIndex === null) {
          const mode: SelectionMode = this.tool === 'default' ? 'residue' : this.mode;
          if (mode === 'chain') { const chain = rec.model.residues.chainIndex[h.info.residueIndex]!; for (let r = 0; r < rec.model.residues.count; r++) if (rec.model.residues.chainIndex[r] === chain) residues.add(r); }
          else residues.add(h.info.residueIndex);
          hoverShells = this.shellsFor(h.info.entrySerial, new Set(), residues, 'hover');
          hoveredRibbonsCleared = true;
        } else {
          hoverShells = this.shellsFor(h.info.entrySerial, atoms, new Set(), 'hover');
          hoveredRibbonsCleared = true;
        }
      }
    }
    else if (this.externalHover) {
      const rec = this.manager.entries.get(this.externalHover.entrySerial);
      if (rec) {
        const atoms = new Set<number>();
        for (const r of this.externalHover.residues) {
          const start = rec.model.residues.atomStart[r], count = rec.model.residues.atomCount[r];
          if (start === undefined || count === undefined) continue;
          for (let i = start; i < start + count; i++) atoms.add(i);
        }
        hoverShells = this.shellsFor(this.externalHover.entrySerial, atoms, this.externalHover.residues, 'hover');
        hoveredRibbonsCleared = true;
      }
    }
    if (!hoveredRibbonsCleared) for (const p of this.manager.passes()) if (p.ribbons) for (const r of p.ribbons) r.setHighlight('hover', null);
    this.hoverLayer.set(hoverShells);
  }

  /** Pivot (TrackballControl.cs:404-423): selection centre ?? hovered atom ?? centre of the entry under the cursor. */
  private pivotAt(clientX: number, clientY: number): Vector3 | null {
    let sx = 0, sy = 0, sz = 0, n = 0;
    for (const [es, atoms] of Object.entries(this.selection)) {
      const rec = this.manager.entries.get(Number(es));
      if (!rec) continue;
      for (const i of atoms) { sx += rec.model.xyz[i * 3]!; sy += rec.model.xyz[i * 3 + 1]!; sz += rec.model.xyz[i * 3 + 2]!; n++; }
    }
    if (n > 0) return domainToWorld(this.transform, sx / n, sy / n, sz / n);
    if (this.hover) return this.hover.world.clone();
    const hit = this.pickAt(clientX, clientY);
    if (hit) {
      const rec = this.manager.entries.get(hit.object.entrySerial);
      if (rec) return domainToWorld(this.transform, rec.bounds.centroid[0], rec.bounds.centroid[1], rec.bounds.centroid[2]);
    }
    return null;
  }

  /**
   * Entry hover box (TrackballControl.HoverableEntry / RaycastEntry / EntryFitsOnScreen, :304-383): the closest entry
   * whose Å bounds the pointer ray hits, if it covers less than INTERACTION.entryHoverBoxCoverage of the viewport.
   * Returns true when the box changed.
   */
  private updateHoveredEntry(clientX: number, clientY: number): boolean {
    const px = this.clientToPixel(clientX, clientY);
    if (!px) return false;
    const ndc = new Vector3((px.x / this.width) * 2 - 1, 1 - (px.y / this.height) * 2, 1);
    const inv = this.projection.clone().invert();
    const farPoint = ndc.applyMatrix4(inv);
    const toDomain = this.root.matrix.clone().invert();
    const origin = new Vector3().applyMatrix4(toDomain);
    const dir = farPoint.normalize().applyMatrix4(toDomain).sub(origin);
    let best: { serial: number; t: number } | null = null;
    for (const [serial, rec] of this.manager.entries) {
      const b = rec.bounds;
      if (b.count === 0 || b.max.every((v, i) => v === b.min[i])) continue;
      const t = rayAabb(origin, dir, b.min, b.max);
      if (t !== null && (!best || t < best.t)) best = { serial, t };
    }
    if (best) {
      const b = this.manager.entries.get(best.serial)!.bounds;
      const corners = boxCornersToView(b.min, b.max, 0, this.root.matrix);
      const coverage = screenCoverage(corners, this.projection);
      if (coverage === null || coverage >= INTERACTION.entryHoverBoxCoverage) best = null;
      else return this.entryBox.set(best.serial, b.min, b.max);
    }
    return this.entryBox.set(null);
  }

  /** Passes for debugging / tests. */
  get passList(): PassSet[] { return [...this.manager.passes()]; }
}
